import { useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { Navigate, useLocation } from 'react-router'
import { Loader2 } from 'lucide-react'
import { supabasePulse } from '../lib/supabasePulse'
import { setPulseAccessContext, setPulseUserContext } from '../lib/sentry'
import { isPulseEmailVerified } from '../lib/emailVerification'
import {
  clearPulseMfaVerification,
  isPulseMfaVerifiedForUser,
} from '../lib/mfa'

interface PulseProtectedRouteProps {
  children: ReactNode
  allowedEmail?: string
}

export function PulseProtectedRoute({
  children,
  allowedEmail = 'contact@aeinsights.org',
}: PulseProtectedRouteProps) {
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState<Session | null>(null)
  const [accessDenied, setAccessDenied] = useState(false)
  const location = useLocation()

  useEffect(() => {
    async function resolveSession(nextSession: Session | null) {
      if (nextSession?.user && !isPulseEmailVerified(nextSession.user)) {
        void supabasePulse.auth.signOut()
        clearPulseMfaVerification()
        setSession(null)
        setAccessDenied(false)
        setLoading(false)
        return
      }

      if (!nextSession) {
        clearPulseMfaVerification()
        setSession(null)
        setAccessDenied(false)
        setLoading(false)
        return
      }

      if (isPulseMfaVerifiedForUser(nextSession.user.id)) {
        const { data: assuranceData, error: assuranceError } =
          await supabasePulse.auth.mfa.getAuthenticatorAssuranceLevel()

        if (assuranceError || assuranceData?.currentLevel !== 'aal2') {
          clearPulseMfaVerification()
        }
      }

      setSession(nextSession)
      setAccessDenied(nextSession.user.email !== allowedEmail)
      setLoading(false)
    }

    supabasePulse.auth.getSession().then(({ data: { session } }) => {
      void resolveSession(session)
    })

    const {
      data: { subscription },
    } = supabasePulse.auth.onAuthStateChange((_event, session) => {
      void resolveSession(session)
    })

    return () => subscription.unsubscribe()
  }, [allowedEmail])

  useEffect(() => {
    setPulseUserContext(
      session
        ? {
            id: session.user.id,
            email: session.user.email ?? null,
          }
        : null,
    )
  }, [session])

  useEffect(() => {
    if (!session) {
      setPulseAccessContext('anonymous')
      return
    }

    setPulseAccessContext(accessDenied ? 'denied' : 'authorized')
  }, [accessDenied, session])

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-theme-cta" />
      </div>
    )
  }

  if (!session) {
    return <Navigate to="/login" replace />
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center p-4">
        <div className="bg-theme-error-bg border border-theme-error/20 rounded-2xl p-8 max-w-md text-center">
          <h1 className="text-2xl font-black text-theme-error mb-2">Access Denied</h1>
          <p className="text-theme-error/80 mb-6">
            Your account ({session.user.email}) is not authorized to access this system.
          </p>
          <button
            onClick={() => supabasePulse.auth.signOut()}
            className="px-6 py-2 bg-theme-error/10 hover:bg-theme-error/20 text-theme-error rounded-lg font-bold transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    )
  }

  if (!isPulseMfaVerifiedForUser(session.user.id)) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to="/mfa/setup" state={{ returnTo }} replace />
  }

  return <>{children}</>
}
